import { ENV } from "@/env";
import { SupportedLocale, DEFAULT_LOCALE, parseSupportedLocales } from "@/shared/culture/locales";
import { resolveLocale } from "@/shared/culture/resolveLocale";
import { loadTranslations } from "@/shared/culture/translationLoader";
import { useThemeStore } from "@/store/themeStore";
import { Link } from "@tanstack/react-router";
import { ThemeProvider, CultureProvider, ToastProvider } from "@vintecc/components";
import { ErrorInfo, FC, useEffect, useState } from "react";
import { ErrorDisplay } from "../ErrorDisplay/ErrorDisplay";
import styles from "../../layout/RootLayout/RootLayout.module.css";

type CrashFallbackProps = {
  error: Error;
  errorInfo: ErrorInfo | null;
};

export const CrashFallbackProviders: FC<CrashFallbackProps> = ({ error, errorInfo }) => {
  const theme = useThemeStore((state) => state.currentThemeConfig);
  const [translations, setTranslations] = useState<Record<string, Record<string, string>>>({});
  const [locale, setLocale] = useState<SupportedLocale>(DEFAULT_LOCALE);

  useEffect(() => {
    const initializeCommonTranslations = async () => {
      const allowedLocales = parseSupportedLocales(ENV.SUPPORTED_LOCALES);
      const resolvedLocale = resolveLocale(allowedLocales);
      setLocale(resolvedLocale);

      try {
        const loadedTranslations = await loadTranslations(resolvedLocale);
        setTranslations(loadedTranslations);
      } catch (loadError) {
        console.error(`Error initializing crash fallback translations for locale ${resolvedLocale}:`, loadError);
      }
    };

    void initializeCommonTranslations();
  }, []);

  return (
    <ThemeProvider theme={theme} linkComponent={Link}>
      <CultureProvider locale={locale} translations={translations}>
        <ToastProvider>
          <div className={styles.root}>
            <header className={styles.top}></header>
            <nav className={styles.nav}></nav>
            <main key={"capture"} className={styles.main}>
              <ErrorDisplay error={{ error }} errorInfo={errorInfo} />
            </main>
          </div>
        </ToastProvider>
      </CultureProvider>
    </ThemeProvider>
  );
};
